import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { cn } from '~/lib/utils';

interface AppPreviewProps {
  slug: string;
  /** "live" → public /apps/<slug>/; "admin" → admin preview that ignores visibility. */
  source?: 'live' | 'admin';
  /** Rendered width in CSS pixels. The iframe itself is rendered at a fixed
   *  virtual viewport and scaled down to this size — keeps app layouts
   *  faithful even at thumbnail dimensions. */
  width?: number;
  /** Rendered height in CSS pixels. Defaults to `width * 0.625` (16:10). */
  height?: number;
  /** Virtual viewport width the iframe believes it has. 1280 keeps most
   *  desktop-leaning apps from collapsing to mobile layouts. */
  virtualWidth?: number;
  className?: string;
  /** Show a thin chrome border + URL bar. Off by default for embedded grid use. */
  showChrome?: boolean;
  title?: string;
  /** Cache buster — pass `updatedAt` so the iframe reloads when content changes. */
  cacheKey?: string | number | null;
  /**
   * A decorative thumbnail: the app inside may not scroll the page or take
   * keyboard focus (FRONT-2511). Only affects `source="admin"`, where the
   * server injects a guard into the previewed document; the live app is
   * served untouched.
   */
  stillFrame?: boolean;
  /** Optional click-through. If omitted, falls back to opening the live URL.
   *  Receives the click event so a caller can tell a plain click from a
   *  modifier click (FRONT-2744); callers that ignore it are unchanged. */
  onOpen?: (e: MouseEvent<HTMLDivElement>) => void;
  /** Accessible name when `onOpen` is set. Defaults to `Open app <title>`. */
  label?: string;
}

const BORDER_PX = 1;
/** Height of the URL strip `showChrome` adds above the app. */
export const PREVIEW_CHROME_PX = 20;

export function AppPreview({
  slug,
  source = 'live',
  width = 320,
  height,
  virtualWidth = 1280,
  className,
  showChrome = false,
  title,
  cacheKey,
  stillFrame = false,
  onOpen,
  label,
}: AppPreviewProps) {
  const finalHeight = height ?? Math.round(width * 0.625);
  // The box is border-box with a 1px border, so the app scales to its inner size.
  const innerWidth = Math.max(1, width - 2 * BORDER_PX);
  const innerHeight = Math.max(1, finalHeight - 2 * BORDER_PX);
  const scale = innerWidth / virtualWidth;
  const virtualHeight = Math.round(innerHeight / scale);
  const cacheSuffix = cacheKey != null ? `?v=${encodeURIComponent(String(cacheKey))}` : '';
  const thumbnailParam = stillFrame ? `${cacheSuffix ? '&' : '?'}thumbnail=1` : '';
  const src = source === 'admin'
    ? `/api/admin/apps/${slug}/preview${cacheSuffix}${thumbnailParam}`
    : `/apps/${slug}/${cacheSuffix}`;

  const [loaded, setLoaded] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Reset loading state when the source URL changes (slug or cacheKey).
  useEffect(() => {
    setLoaded(false);
  }, [src]);

  return (
    <div
      className={cn(
        'relative overflow-hidden bg-white dark:bg-zinc-950 border border-border rounded-md',
        showChrome && 'pt-5',
        onOpen && 'cursor-pointer hover:border-primary/50 transition-colors',
        className,
      )}
      style={{ width, height: showChrome ? finalHeight + PREVIEW_CHROME_PX : finalHeight }}
      onClick={onOpen}
      role={onOpen ? 'button' : undefined}
      aria-label={onOpen ? (label ?? `Open app ${title ?? slug}`) : undefined}
    >
      {showChrome && (
        <div className="absolute top-0 inset-x-0 h-5 bg-muted border-b border-border flex items-center px-2 gap-1">
          <span className="size-1.5 rounded-full bg-zinc-400/60" />
          <span className="size-1.5 rounded-full bg-zinc-400/60" />
          <span className="size-1.5 rounded-full bg-zinc-400/60" />
          <span className="ml-1.5 text-[length:var(--text-2xs)] font-mono text-muted-foreground truncate">
            /apps/{slug}/
          </span>
        </div>
      )}
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center text-[length:var(--text-2xs)] text-muted-foreground">
          Loading…
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={src}
        title={title ?? `Preview of ${slug}`}
        // Live previews load same-origin HTML we serve ourselves, so they
        // need `allow-same-origin` — otherwise the iframe runs at an opaque
        // origin and any self-navigation, SDK fetch to `/api/apps/…`, or
        // internal link triggers "Unsafe attempt to load URL …" in the
        // console. Admin previews stay locked down (drafts can be arbitrary
        // user-authored HTML and shouldn't get our cookies).
        sandbox={source === 'live' ? 'allow-scripts allow-same-origin' : 'allow-scripts'}
        // A preview never makes sound on its own: an app that plays a chime on
        // load would otherwise play it from every card and list that shows it.
        // Thumbnails also get a script guard for `play()` and Web Audio
        // (`THUMBNAIL_GUARD_SCRIPT`, admin source only).
        allow="autoplay 'none'"
        loading="lazy"
        onLoad={() => setLoaded(true)}
        style={{
          width: virtualWidth,
          height: virtualHeight,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          border: 0,
          pointerEvents: onOpen ? 'none' : 'auto',
        }}
      />
    </div>
  );
}
